import { AllyUtil } from "../util/ally-util.class";
import { AllyBridge } from "./ally-bridge.class";

/**
 * The AllyDestroyer class used in the AllyBase class.
 *
 * All AllyDestroyer objects form a hierarchical tree structure.
 *
 * Each object has a parent object, when the parent object is destroyed the child objects are destroyed aswell.
 *
 * The parent objects bridge has to be provided to the constructor of the child object.
 *
 * @extends AllyBridge<AllyDestroyer>
 * @see AllyBridge
 */
export class AllyDestroyer extends AllyBridge<AllyDestroyer> {
    /**
     * The callbacks to be run when the destroy function ist triggered.
     */
    protected callbacks: (() => void)[];
    /**
     * The callbacks to be run when the destroy function ist triggered that trigger the destroy function of the child AllyDestroyer objects.
     */
    protected destroyerCallbacks: (() => void)[];
    private parentBridge: () => AllyDestroyer | null | undefined;
    private detachFromParent: (() => void) | undefined;

    /**
     * @param brigde The bridge to the parent AllyDestroyer object.
     */
    constructor(brigde: () => AllyDestroyer | null | undefined) {
        super(() => this);
        this.callbacks = [];
        this.destroyerCallbacks = [];
        this.parentBridge = brigde;

        AllyUtil.delay(!brigde(), () => {
            this.attachToParent();
        });
    }

    /**
     * Add a callback to the list of callback that will be executed when the destroy function is called.
     * @param callback The callback to be added.
     */
    public addCallback(callback: () => void) {
        this.callbacks.push(callback);
    }

    public removeCallback(callback: () => void) {
        const index = this.callbacks ? this.callbacks.indexOf(callback) : -1;
        if (index !== -1) {
            this.callbacks.splice(index, 1);
        }
    }

    public get destroyed(): boolean {
        return this.callbacks === undefined;
    }

    /** Moves this object below another parent; it is then destroyed with that parent instead of the old one. */
    public reparent(brigde: () => AllyDestroyer | null | undefined) {
        if (!this.callbacks) {
            return;
        }
        this.detachFromParent?.();
        this.parentBridge = brigde;
        this.attachToParent();
    }

    /**
     * Executes all callbacks that were set via the addCallback function and triggers the destroy function of all child AllyDestroyer objects.
     *
     * The AllyDestroyer object will become unusable once this function was run.
     */
    public destroy() {
        if (this.callbacks) {
            AllyUtil.forEach(this.callbacks, (callback) => {
                callback();
            });
            AllyUtil.forEach(this.destroyerCallbacks, (callback) => {
                callback();
            });
            this.callbacks = undefined as unknown as AllyDestroyer["callbacks"];
            this.destroyerCallbacks = undefined as unknown as AllyDestroyer["destroyerCallbacks"];
        }
    }

    private attachToParent() {
        if (!this.callbacks) {
            return;
        }
        const parent = this.parentBridge();
        if (!parent) {
            return;
        }
        if (!parent.callbacks) {
            this.destroy();
            return;
        }
        const callbackParent = () => { this.destroy(); };
        const callbackChild = () => {
            let index = parent.destroyerCallbacks ? parent.destroyerCallbacks.indexOf(callbackParent) : -1;
            if (index !== -1) {
                parent.destroyerCallbacks.splice(index, 1);
            }
            index = this.callbacks ? this.callbacks.indexOf(callbackChild) : -1;
            if (index !== -1) {
                this.callbacks.splice(index, 1);
            }
            this.detachFromParent = undefined;
        };
        parent.destroyerCallbacks.push(callbackParent);
        this.addCallback(callbackChild);
        this.detachFromParent = callbackChild;
    }
}
